import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import CSVUploadModal from '../Invoicing/CSVUploadModal';
import AmountInput from '../ui/AmountInput';
import '../CRMProfileScreen.css';
import { CRMTabHero, CRMSectionHeader, TabIcons } from './CRMTabUI';

// Template column labels match the user-facing form field names exactly so the
// downloaded template doubles as a reference. The backend header-map accepts
// these labels as well as the legacy snake_case columns.
const CREDIT_TERMS_TEMPLATE_COLUMNS = ['Partner Credit Days', 'Credit Limit', 'Risk Rating'];

// Sample data row baked into the downloaded template so users see the expected
// formats at a glance (integer days, decimal limit, one of the four ratings).
const CREDIT_TERMS_TEMPLATE_EXAMPLE = ['30', '250000.00', 'Low'];

// Inline hints rendered inside the upload modal beneath the column tags.
const CREDIT_TERMS_TEMPLATE_NOTES = [
    'Partner Credit Days: whole number of days (e.g. 30, 45, 60).',
    'Credit Limit: decimal amount in USD, no currency symbol (e.g. 250000.00).',
    'Risk Rating: one of Zero, Low, Medium, High.',
];

/**
 * CRM Credit Rating tab.
 *
 * Phase 1 scope (locked in with PM):
 *  - Per-profile CSV upload populates Partner Credit Days, Credit Limit, Risk Rating.
 *  - All other credit fields are manually editable on this tab.
 *  - Credit Balance Indicator is computed read-only on the backend.
 *  - "Credit Exceed" auto-derives a level from balance vs limit; user may override.
 *
 * Phase 2 (Credit Approval workflow):
 *  - "Add More Credit" now submits a CreditIncreaseRequest instead of mutating
 *    the credit_limit directly. The limit is only bumped when a Credit Approver
 *    approves the request.
 *  - A "Pending request" pill appears next to the limit while a request is open
 *    and a second submission is blocked client-side (server enforces this too).
 */

const RATING_OPTIONS = ['Zero', 'Low', 'Medium', 'High'];
const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

// Allocation segment metadata. Drives both the bar segments and the legend cards
// so they stay in lock-step (color, label, description, hover sync).
const SEGMENT_META = [
    {
        key: 'enquiry',
        label: 'Enquiry',
        description: 'Open enquiries at Bid / Offer stage',
    },
    {
        key: 'pending',
        label: 'Pending',
        description: 'Signed deals awaiting BL / BDN delivery',
    },
    {
        key: 'actualized',
        label: 'Actualized',
        description: 'Delivered, awaiting invoice payment',
    },
    {
        key: 'balance',
        label: 'Available Balance',
        description: 'Headroom remaining on the credit limit',
    },
];

// Auto-derivation thresholds used when balance < 0 (override-able by user via the dropdown).
const deriveCreditExceedLevel = (limit, balance) => {
    if (limit === null || limit === undefined || Number(limit) === 0) return 'Zero';
    if (balance === null || balance === undefined) return 'Zero';
    const balanceNum = Number(balance);
    if (balanceNum >= 0) return 'Zero';
    const ratio = Math.abs(balanceNum) / Math.abs(Number(limit));
    if (ratio <= 0.25) return 'Low';
    if (ratio <= 0.5) return 'Medium';
    return 'High';
};

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (!Number.isFinite(num)) return '-';
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AddMoreCreditModal = ({
    open,
    currentLimit,
    onClose,
    onSubmit,
    isSubmitting,
    errorMessage,
    hasPendingRequest,
}) => {
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [dealReference, setDealReference] = useState('');

    useEffect(() => {
        if (!open) {
            setAmount('');
            setReason('');
            setDealReference('');
        }
    }, [open]);

    if (!open) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (hasPendingRequest) return;
        const numericAmount = Number(amount);
        if (!Number.isFinite(numericAmount) || numericAmount <= 0) return;
        if (!reason.trim()) return;
        onSubmit(numericAmount, reason.trim(), dealReference.trim());
    };

    const newLimit =
        toNumber(currentLimit) + (Number.isFinite(Number(amount)) ? Number(amount) : 0);

    return (
        <div
            className="crm-credit-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-more-credit-title"
        >
            <form className="crm-credit-modal" onSubmit={handleSubmit}>
                <div className="crm-credit-modal-header">
                    <span id="add-more-credit-title" className="crm-credit-modal-title">
                        Request More Credit
                    </span>
                    <button
                        type="button"
                        className="crm-credit-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </div>
                <div className="crm-credit-modal-body">
                    <div className="crm-credit-modal-help">
                        Submits a Credit Increase Request to the Credit Approvers. The
                        partner's Credit Limit will only change once the request is
                        approved.
                    </div>
                    {hasPendingRequest && (
                        <div className="crm-credit-modal-error" role="alert">
                            A pending credit increase request already exists for this
                            partner. Wait for it to be reviewed before submitting another.
                        </div>
                    )}
                    <div className="crm-input-row">
                        <div className="crm-input-group half-width">
                            <label className="crm-input-label">Current Limit</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formatCurrency(currentLimit)}
                                readOnly
                            />
                        </div>
                        <div className="crm-input-group half-width">
                            <label className="crm-input-label required">
                                Requested Increase
                            </label>
                            <AmountInput
                                className="crm-input"
                                value={amount}
                                onChange={setAmount}
                                disabled={isSubmitting || hasPendingRequest}
                                required
                            />
                        </div>
                    </div>
                    <div className="crm-input-group full-width">
                        <label className="crm-input-label">
                            New Limit (if approved)
                        </label>
                        <input
                            type="text"
                            className="crm-input"
                            value={formatCurrency(newLimit)}
                            readOnly
                        />
                    </div>
                    <div className="crm-input-group full-width">
                        <label className="crm-input-label">Deal Reference (optional)</label>
                        <input
                            type="text"
                            className="crm-input"
                            value={dealReference}
                            onChange={(e) => setDealReference(e.target.value)}
                            placeholder="e.g. RFQ #1234, Vessel MV NEPTUNE"
                            disabled={isSubmitting || hasPendingRequest}
                            maxLength={100}
                        />
                    </div>
                    <div className="crm-input-group full-width">
                        <label className="crm-input-label required">Reason</label>
                        <textarea
                            className="crm-input crm-credit-modal-textarea"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Why is the credit limit being increased?"
                            disabled={isSubmitting || hasPendingRequest}
                            required
                        />
                    </div>
                    {errorMessage && (
                        <div className="crm-credit-modal-error" role="alert">
                            {errorMessage}
                        </div>
                    )}
                </div>
                <div className="crm-credit-modal-footer">
                    <button
                        type="button"
                        className="crm-credit-action-btn crm-credit-action-btn--secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="crm-credit-action-btn crm-credit-action-btn--primary"
                        disabled={
                            isSubmitting ||
                            hasPendingRequest ||
                            !amount ||
                            Number(amount) <= 0 ||
                            !reason.trim()
                        }
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                </div>
            </form>
        </div>
    );
};

const CRMCreditRatingTab = ({ profileData, onDataChange, isNewProfile, isCreditApprover = false }) => {
    const { showError, showSuccess } = useFlashMessage();

    const [formData, setFormData] = useState({
        partner_credit_days: '',
        credit_limit: '',
        credit_risk_rating: '',
        credit_status: 'pending',
        credit_exceed_level: 'Zero',
        enquiry_credit_amount: 0,
        pending_credit_amount: 0,
        actualized_credit_amount: 0,
        credit_balance: null,
    });

    const [csvModalOpen, setCsvModalOpen] = useState(false);
    const [addCreditModalOpen, setAddCreditModalOpen] = useState(false);
    const [addCreditSubmitting, setAddCreditSubmitting] = useState(false);
    const [addCreditError, setAddCreditError] = useState(null);
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [pendingCreditRequest, setPendingCreditRequest] = useState(null);

    // Look up any open CreditIncreaseRequest so we can disable resubmission and
    // surface a "Pending request" pill next to the Credit Limit field.
    const refreshPendingCreditRequest = async () => {
        if (!profileData?.id) {
            setPendingCreditRequest(null);
            return;
        }
        try {
            // Scope to BASE-LIMIT requests only. An auto-opened per-enquiry
            // ``enquiry_uplift`` CIR is also ``status=pending`` but must NOT
            // block the manual "Add More Credit" (base-limit) flow -- the
            // backend ``perform_create`` only rejects a pending *base_limit*
            // request, so the FE gate here has to match that scope.
            const resp = await axiosInstance.get(
                `/api/crm/credit-requests/?profile=${profileData.id}&status=pending&request_type=base_limit`
            );
            const list = Array.isArray(resp.data)
                ? resp.data
                : Array.isArray(resp.data?.results)
                ? resp.data.results
                : [];
            setPendingCreditRequest(list.length > 0 ? list[0] : null);
        } catch (err) {
            // Don't block the tab from rendering if this lookup fails.
            // eslint-disable-next-line no-console
            console.warn('[CreditRating] Failed to fetch pending credit requests', err);
            setPendingCreditRequest(null);
        }
    };

    useEffect(() => {
        refreshPendingCreditRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileData?.id]);

    useEffect(() => {
        if (!profileData) return;
        setFormData((prev) => ({
            ...prev,
            partner_credit_days:
                profileData.partner_credit_days ?? prev.partner_credit_days ?? '',
            credit_limit: profileData.credit_limit ?? prev.credit_limit ?? '',
            credit_risk_rating:
                profileData.credit_risk_rating ?? prev.credit_risk_rating ?? '',
            credit_status: profileData.credit_status || 'pending',
            credit_exceed_level: profileData.credit_exceed_level || 'Zero',
            enquiry_credit_amount: profileData.enquiry_credit_amount ?? 0,
            pending_credit_amount: profileData.pending_credit_amount ?? 0,
            actualized_credit_amount: profileData.actualized_credit_amount ?? 0,
            credit_balance:
                profileData.credit_balance !== undefined
                    ? profileData.credit_balance
                    : prev.credit_balance,
        }));
    }, [profileData]);

    // Locally compute the balance for the progress bar (the canonical value
    // still comes back from the backend serializer on next refresh).
    const computedBalance = useMemo(() => {
        if (formData.credit_limit === '' || formData.credit_limit === null) return null;
        const limitNum = toNumber(formData.credit_limit);
        const used =
            toNumber(formData.enquiry_credit_amount) +
            toNumber(formData.pending_credit_amount) +
            toNumber(formData.actualized_credit_amount);
        return limitNum - used;
    }, [
        formData.credit_limit,
        formData.enquiry_credit_amount,
        formData.pending_credit_amount,
        formData.actualized_credit_amount,
    ]);

    // Auto-derive Credit Exceed when balance is negative, unless user explicitly set it.
    // We only auto-set when level is currently 'Zero' (default) to avoid clobbering an override.
    // Intentional: deps exclude credit_exceed_level and onDataChange to prevent a feedback
    // loop where setting the level re-triggers this effect.
    useEffect(() => {
        if (computedBalance === null) return;
        const derived = deriveCreditExceedLevel(formData.credit_limit, computedBalance);
        if (
            derived !== 'Zero' &&
            formData.credit_exceed_level === 'Zero' &&
            derived !== formData.credit_exceed_level
        ) {
            setFormData((prev) => ({ ...prev, credit_exceed_level: derived }));
            if (onDataChange) onDataChange('credit_exceed_level', derived);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedBalance, formData.credit_limit]);

    // Parent CRMProfileScreen expects (field, value) - NOT a single object.
    // Passing an object would be treated as a section name and break auto-save.
    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (onDataChange) onDataChange(field, value);
    };

    // Shared CSVUploadModal expects onUpload to RETURN the response payload
    // (or throw) so the modal can render its own success/error UI. We keep
    // the local state in sync with whatever the backend confirms was applied.
    const handleCsvUpload = async (file) => {
        if (!profileData?.id) {
            const msg = 'Profile must be saved before uploading credit terms.';
            showError(msg);
            throw new Error(msg);
        }
        const payload = new FormData();
        payload.append('file', file);
        const response = await axiosInstance.post(
            `/api/crm/profiles/${profileData.id}/credit-terms-csv/`,
            payload,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const data = response.data || {};
        const updates = {
            partner_credit_days: data.partner_credit_days ?? '',
            credit_limit: data.credit_limit ?? '',
            credit_risk_rating: data.credit_risk_rating ?? '',
        };
        setFormData((prev) => ({ ...prev, ...updates }));
        if (onDataChange) {
            Object.entries(updates).forEach(([field, value]) => {
                onDataChange(field, value);
            });
        }
        return data;
    };

    const handleAddMoreCredit = async (amount, reason, dealReference) => {
        if (!profileData?.id) {
            setAddCreditError('Profile must be saved before requesting more credit.');
            return;
        }
        if (pendingCreditRequest) {
            setAddCreditError(
                'A pending credit increase request already exists for this partner.'
            );
            return;
        }
        setAddCreditSubmitting(true);
        setAddCreditError(null);
        try {
            const resp = await axiosInstance.post('/api/crm/credit-requests/', {
                profile: profileData.id,
                requested_amount: amount,
                reason,
                deal_reference: dealReference || '',
            });
            setPendingCreditRequest(resp.data || null);
            showSuccess(
                `Credit increase request submitted for ${formatCurrency(amount)}. ` +
                'A Credit Approver will review it.'
            );
            setAddCreditModalOpen(false);
        } catch (err) {
            const message =
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                err?.response?.data?.requested_amount?.[0] ||
                getErrorMessage(err) ||
                'Failed to submit credit increase request.';
            setAddCreditError(message);
            showError(message);
        } finally {
            setAddCreditSubmitting(false);
        }
    };

    // Progress bar segment widths (cap totals at 100%).
    const limitNum = toNumber(formData.credit_limit);
    const enquiryNum = toNumber(formData.enquiry_credit_amount);
    const pendingNum = toNumber(formData.pending_credit_amount);
    const actualizedNum = toNumber(formData.actualized_credit_amount);
    const balanceNum = computedBalance ?? 0;
    const usedNum = enquiryNum + pendingNum + actualizedNum;

    const pct = (value) => {
        if (limitNum <= 0) return 0;
        return Math.max(0, Math.min(100, (Math.max(0, value) / limitNum) * 100));
    };

    const enquiryPct = pct(enquiryNum);
    const pendingPct = pct(pendingNum);
    const actualizedPct = pct(actualizedNum);
    const balancePct = Math.max(
        0,
        100 - enquiryPct - pendingPct - actualizedPct
    );
    const usedPct = Math.min(100, enquiryPct + pendingPct + actualizedPct);

    const balanceIsNegative = computedBalance !== null && computedBalance < 0;

    // Credit Exceed (PM request): the dollar amount the partner is OVER their
    // Credit Limit, i.e. credit that is being used/requested for deals but has
    // not been formally approved as a limit increase. Equivalent to
    // max(0, Used - Limit) or max(0, -computedBalance).
    const overLimitAmount =
        computedBalance !== null && computedBalance < 0
            ? Math.abs(computedBalance)
            : 0;
            
    // Per-segment values for the bar + legend cards. `offset` is the cumulative
    // left edge so each segment can be absolutely positioned without recomputing
    // the running total inline in JSX.
    const segmentValues = {
        enquiry: { amount: enquiryNum, pct: enquiryPct, offset: 0 },
        pending: {
            amount: pendingNum,
            pct: pendingPct,
            offset: enquiryPct,
        },
        actualized: {
            amount: actualizedNum,
            pct: actualizedPct,
            offset: enquiryPct + pendingPct,
        },
        balance: {
            amount: balanceNum,
            pct: balancePct,
            offset: enquiryPct + pendingPct + actualizedPct,
        },
    };

    // Read-only Shared Group Credit indicator (computed on the backend). Only
    // shown when this partner is grouped, or has links that aren't pooled yet.
    // Never feeds the editable credit_limit input above -- it stays this
    // partner's OWN base limit; the group figure is display-only.
    const groupCredit = profileData?.group_credit;
    const showGroupCredit = Boolean(
        groupCredit && (groupCredit.is_grouped === true || groupCredit.has_links === true)
    );
    const groupCurrency = groupCredit?.display_currency || '';
    const groupMembers = Array.isArray(groupCredit?.members) ? groupCredit.members : [];
    const groupBalanceNegative =
        groupCredit?.is_grouped &&
        Number(groupCredit.group_available_balance) < 0;

    return (
        <div className="crm-tab-panel crm-tab-modern">
            <CRMTabHero
                tone="emerald"
                icon={<TabIcons.CreditCard />}
                title="Credit Rating & Limits"
                description="Credit terms, limits, risk rating, and credit lifecycle status for this partner."
            />
            <div className="crm-form-section">
                <div className="crm-section-header">
                    <div className="crm-tab-section-head-titleblock">
                        <span className="crm-tab-section-head-icon" data-tone="emerald" aria-hidden="true">
                            <TabIcons.CreditCard />
                        </span>
                        <h2 className="crm-tab-section-head-title">Credit Terms</h2>
                    </div>
                    <div className="crm-input-group" style={{ minWidth: 220, flex: 'unset' }}>
                        <label className="crm-input-label">Credit Status</label>
                        <select
                            className="crm-input crm-credit-status-select"
                            value={formData.credit_status}
                            data-status={formData.credit_status}
                            onChange={(e) => handleInputChange('credit_status', e.target.value)}
                            disabled={isNewProfile || !isCreditApprover}
                            title={
                                !isCreditApprover
                                    ? 'Only Credit Approvers can change Credit Status.'
                                    : undefined
                            }
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {!isCreditApprover && (
                            <small className="crm-credit-field-hint">
                                View only - editable by Credit Approvers.
                            </small>
                        )}
                    </div>
                </div>

                <div className="crm-input-row">
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Partner Credit Days</label>
                        <AmountInput
                            kind="integer"
                            className="crm-input"
                            value={formData.partner_credit_days ?? ''}
                            onChange={(v) =>
                                handleInputChange(
                                    'partner_credit_days',
                                    v === '' ? null : v
                                )
                            }
                            placeholder="e.g. 30"
                            disabled={isNewProfile}
                        />
                        <small className="crm-credit-field-hint">
                            Number of days credit is extended to this partner.
                        </small>
                    </div>
                    <div className="crm-input-group half-width">
                        <div className="crm-credit-label-row">
                            <label className="crm-input-label">Credit Limit</label>
                            {pendingCreditRequest && (
                                <span
                                    className="crm-credit-pending-pill"
                                    title={
                                        pendingCreditRequest.reason
                                            ? `Reason: ${pendingCreditRequest.reason}`
                                            : 'Pending Credit Approver review'
                                    }
                                >
                                    Pending request:{' '}
                                    {formatCurrency(pendingCreditRequest.requested_amount)}
                                </span>
                            )}
                        </div>
                        <AmountInput
                            className="crm-input"
                            value={formData.credit_limit ?? ''}
                            onChange={(v) =>
                                handleInputChange(
                                    'credit_limit',
                                    v === '' ? null : v
                                )
                            }
                            placeholder="e.g. 250,000.00"
                            disabled={isNewProfile}
                        />
                        <small className="crm-credit-field-hint">
                            Maximum credit available to the partner (USD).
                        </small>
                    </div>
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Risk Rating</label>
                        <select
                            className="crm-input"
                            value={formData.credit_risk_rating || ''}
                            onChange={(e) =>
                                handleInputChange('credit_risk_rating', e.target.value || null)
                            }
                            disabled={isNewProfile}
                        >
                            <option value="">Zero / Low / Medium / High</option>
                            {RATING_OPTIONS.map((rating) => (
                                <option key={rating} value={rating}>
                                    {rating}
                                </option>
                            ))}
                        </select>
                        <small className="crm-credit-field-hint">
                            Credit-specific rating; independent from the Risk Engine score.
                        </small>
                    </div>
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Credit Exceed</label>
                        <input
                            type="text"
                            className="crm-input crm-credit-amount-input crm-credit-exceed-input"
                            data-segment="exceed"
                            data-over={overLimitAmount > 0 ? 'true' : 'false'}
                            value={
                                overLimitAmount > 0 ? formatCurrency(overLimitAmount) : ''
                            }
                            placeholder="No credit exceeded"
                            readOnly
                        />
                        <small className="crm-credit-field-hint">
                            Credit used beyond the limit (requested but not yet approved).
                        </small>
                    </div>
                </div>

                <div className="crm-input-row">
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Enquiry Credit Request</label>
                        <input
                            type="text"
                            className="crm-input crm-credit-amount-input"
                            data-segment="enquiry"
                            value={formatCurrency(formData.enquiry_credit_amount ?? 0)}
                            readOnly
                        />
                        <small className="crm-credit-field-hint">
                            Auto-derived from open enquiries (Bid / Offer stage). Read-only.
                        </small>
                    </div>
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Pending Credit</label>
                        <input
                            type="text"
                            className="crm-input crm-credit-amount-input"
                            data-segment="pending"
                            value={formatCurrency(formData.pending_credit_amount ?? 0)}
                            readOnly
                        />
                        <small className="crm-credit-field-hint">
                            Auto-derived from signed deal to delivery (BL / BDN). Read-only.
                        </small>
                    </div>
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Actualized Credit</label>
                        <input
                            type="text"
                            className="crm-input crm-credit-amount-input"
                            data-segment="actualized"
                            value={formatCurrency(formData.actualized_credit_amount ?? 0)}
                            readOnly
                        />
                        <small className="crm-credit-field-hint">
                            Auto-derived from delivery (BL / BDN) to invoice payment. Read-only.
                        </small>
                    </div>
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Credit Balance Indicator</label>
                        <input
                            type="text"
                            className="crm-input crm-credit-amount-input"
                            data-segment="balance"
                            data-negative={balanceIsNegative ? 'true' : 'false'}
                            value={
                                computedBalance === null ? '' : formatCurrency(computedBalance)
                            }
                            placeholder="Auto-calculated"
                            readOnly
                        />
                        <small className="crm-credit-field-hint">
                            Auto-computed: Credit Limit minus Enquiry + Pending + Actualized.
                        </small>
                    </div>
                </div>

                <div
                    className="crm-credit-allocation"
                    onMouseLeave={() => setHoveredSegment(null)}
                >
                    <div className="crm-credit-allocation-header">
                        <div>
                            <h3 className="crm-credit-allocation-title">
                                Credit Allocation Breakdown
                            </h3>
                            <p className="crm-credit-allocation-subtitle">
                                Hover a segment or card to see its details.
                            </p>
                        </div>
                        <div className="crm-credit-allocation-summary">
                            <div className="crm-credit-allocation-summary-item">
                                <span className="crm-credit-allocation-summary-label">Used</span>
                                <span className="crm-credit-allocation-summary-value">
                                    {formatCurrency(usedNum)}
                                </span>
                            </div>
                            <div className="crm-credit-allocation-summary-divider" />
                            <div className="crm-credit-allocation-summary-item">
                                <span className="crm-credit-allocation-summary-label">Limit</span>
                                <span className="crm-credit-allocation-summary-value">
                                    {limitNum > 0 ? formatCurrency(limitNum) : '-'}
                                </span>
                            </div>
                            <div className="crm-credit-allocation-summary-divider" />
                            <div className="crm-credit-allocation-summary-item">
                                <span className="crm-credit-allocation-summary-label">
                                    Utilised
                                </span>
                                <span
                                    className="crm-credit-allocation-summary-value"
                                    data-state={
                                        balanceIsNegative
                                            ? 'over'
                                            : usedPct >= 80
                                            ? 'warning'
                                            : 'ok'
                                    }
                                >
                                    {limitNum > 0 ? `${usedPct.toFixed(1)}%` : '-'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div
                        className="crm-credit-progress-bar"
                        role="progressbar"
                        aria-label="Credit allocation breakdown"
                        aria-valuemin="0"
                        aria-valuemax={limitNum > 0 ? limitNum : 1}
                        aria-valuenow={usedNum}
                    >
                        {SEGMENT_META.map((seg) => {
                            const v = segmentValues[seg.key];
                            return (
                                <div
                                    key={seg.key}
                                    className={`crm-credit-progress-segment crm-credit-progress-segment--${seg.key}`}
                                    style={{ left: `${v.offset}%`, width: `${v.pct}%` }}
                                    data-active={hoveredSegment === seg.key ? 'true' : 'false'}
                                    data-dimmed={
                                        hoveredSegment !== null && hoveredSegment !== seg.key
                                            ? 'true'
                                            : 'false'
                                    }
                                    onMouseEnter={() => setHoveredSegment(seg.key)}
                                    title={`${seg.label}: ${formatCurrency(v.amount)} (${v.pct.toFixed(1)}%)`}
                                />
                            );
                        })}
                    </div>

                    <div className="crm-credit-progress-legend">
                        {SEGMENT_META.map((seg) => {
                            const v = segmentValues[seg.key];
                            return (
                                <button
                                    type="button"
                                    key={seg.key}
                                    className="crm-credit-progress-legend-item"
                                    data-segment={seg.key}
                                    data-active={
                                        hoveredSegment === seg.key ? 'true' : 'false'
                                    }
                                    onMouseEnter={() => setHoveredSegment(seg.key)}
                                    onMouseLeave={() => setHoveredSegment(null)}
                                    onFocus={() => setHoveredSegment(seg.key)}
                                    onBlur={() => setHoveredSegment(null)}
                                >
                                    <span
                                        className="crm-credit-progress-legend-swatch"
                                        data-segment={seg.key}
                                    />
                                    <span className="crm-credit-progress-legend-body">
                                        <span className="crm-credit-progress-legend-name">
                                            {seg.label}
                                        </span>
                                        <span className="crm-credit-progress-legend-amount">
                                            {formatCurrency(v.amount)}
                                        </span>
                                        <span className="crm-credit-progress-legend-pct">
                                            {limitNum > 0 ? `${v.pct.toFixed(1)}%` : '-'}
                                        </span>
                                        <span className="crm-credit-progress-legend-desc">
                                            {seg.description}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {limitNum <= 0 && (
                        <small className="crm-credit-progress-note">
                            Set a Credit Limit to populate the bar with real allocation values.
                        </small>
                    )}
                </div>

                <div className="crm-credit-actions">
                    <button
                        type="button"
                        className="crm-credit-action-btn crm-credit-action-btn--secondary"
                        onClick={() => setCsvModalOpen(true)}
                        disabled={isNewProfile}
                    >
                        Upload Credit Terms CSV
                    </button>
                    <button
                        type="button"
                        className="crm-credit-action-btn crm-credit-action-btn--primary"
                        onClick={() => {
                            setAddCreditError(null);
                            setAddCreditModalOpen(true);
                        }}
                        disabled={isNewProfile || Boolean(pendingCreditRequest)}
                        title={
                            pendingCreditRequest
                                ? 'A credit increase request is already pending review.'
                                : undefined
                        }
                    >
                        {pendingCreditRequest
                            ? 'Request Pending...'
                            : '+ Request More Credit'}
                    </button>
                </div>
            </div>

            {showGroupCredit && (
                <div className="crm-form-section crm-group-credit">
                    <div className="crm-group-credit-banner">
                        <span className="crm-group-credit-banner-icon" aria-hidden="true">
                            <TabIcons.CreditCard />
                        </span>
                        <div className="crm-group-credit-banner-text">
                            <span className="crm-group-credit-banner-title">
                                Shared Group Credit
                            </span>
                            <span className="crm-group-credit-banner-sub">
                                Internal risk indicator &mdash; invoices remain per company.
                            </span>
                        </div>
                    </div>

                    {groupCredit.is_grouped ? (
                        <div className="crm-group-credit-totals">
                            <div className="crm-group-credit-total">
                                <span className="crm-group-credit-total-label">
                                    Group Credit Limit
                                </span>
                                <span className="crm-group-credit-total-value">
                                    {formatCurrency(groupCredit.group_credit_limit)}
                                    {groupCurrency ? ` ${groupCurrency}` : ''}
                                </span>
                            </div>
                            <div className="crm-group-credit-total">
                                <span className="crm-group-credit-total-label">
                                    Group Available Balance
                                </span>
                                <span
                                    className="crm-group-credit-total-value"
                                    data-negative={groupBalanceNegative ? 'true' : 'false'}
                                >
                                    {formatCurrency(groupCredit.group_available_balance)}
                                    {groupCurrency ? ` ${groupCurrency}` : ''}
                                </span>
                            </div>
                            <div className="crm-group-credit-total">
                                <span className="crm-group-credit-total-label">
                                    Members Pooled
                                </span>
                                <span className="crm-group-credit-total-value">
                                    {groupCredit.member_count}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <p className="crm-group-credit-empty-note">
                            Linked partners exist but nothing is pooled into a group credit
                            limit yet. See each member&rsquo;s status below.
                        </p>
                    )}

                    {groupMembers.length > 0 && (
                        <div className="crm-group-credit-table-wrap">
                            <table className="crm-group-credit-table">
                                <thead>
                                    <tr>
                                        <th>Member</th>
                                        <th>Credit Limit</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupMembers.map((member) => (
                                        <tr
                                            key={member.profile_id}
                                            data-anchor={member.is_anchor ? 'true' : 'false'}
                                        >
                                            <td>
                                                <span className="crm-group-credit-member-name">
                                                    {member.name}
                                                </span>
                                                {member.is_anchor && (
                                                    <span className="crm-group-credit-anchor-tag">
                                                        This partner
                                                    </span>
                                                )}
                                            </td>
                                            <td className="crm-group-credit-member-limit">
                                                {formatCurrency(member.credit_limit_display)}
                                                {groupCurrency ? ` ${groupCurrency}` : ''}
                                                {member.currency &&
                                                    member.currency !== groupCurrency && (
                                                        <span className="crm-group-credit-member-native">
                                                            {` (${formatCurrency(member.credit_limit)} ${member.currency})`}
                                                        </span>
                                                    )}
                                            </td>
                                            <td>
                                                {member.is_anchor ? (
                                                    <span
                                                        className="crm-group-credit-badge"
                                                        data-state="anchor"
                                                    >
                                                        Anchor
                                                    </span>
                                                ) : member.included ? (
                                                    <span
                                                        className="crm-group-credit-badge"
                                                        data-state="pooled"
                                                    >
                                                        ✓ Pooled
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="crm-group-credit-badge"
                                                        data-state="excluded"
                                                        title={member.reason_text || undefined}
                                                    >
                                                        {member.reason_text || 'Not counted'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <CSVUploadModal
                isOpen={csvModalOpen}
                onClose={() => setCsvModalOpen(false)}
                onUpload={handleCsvUpload}
                title="Upload Credit Terms"
                templateColumns={CREDIT_TERMS_TEMPLATE_COLUMNS}
                templateExampleRow={CREDIT_TERMS_TEMPLATE_EXAMPLE}
                templateNotes={CREDIT_TERMS_TEMPLATE_NOTES}
                entityRequired={false}
            />

            <AddMoreCreditModal
                open={addCreditModalOpen}
                currentLimit={formData.credit_limit}
                onClose={() => setAddCreditModalOpen(false)}
                onSubmit={handleAddMoreCredit}
                isSubmitting={addCreditSubmitting}
                errorMessage={addCreditError}
                hasPendingRequest={Boolean(pendingCreditRequest)}
            />
        </div>
    );
};

export default CRMCreditRatingTab;
